import React, { useState } from 'react';
import {
  createAvailsForm,
  sendAvailsEmail,
  ingestAvails,
  CreateFormResult,
  SendEmailResult,
  IngestResult,
  StaffBreakdownRow,
} from '../services/availsAdminApi';
import './AvailsManagement.css';

type TabId = 'create' | 'send' | 'ingest';
type FormType = 'comics' | 'staff';

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

function nextMonthValue(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function monthValueToName(yyyyMm: string): string {
  const [year, month] = yyyyMm.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long' });
}

function extractSheetId(input: string): string {
  // Accept either a full Drive URL or a raw sheet ID
  const m = input.match(/\/d\/([a-zA-Z0-9_-]+)/);
  return m ? m[1] : input.trim();
}

// ------------------------------------------------------------------
// Create Form Tab
// ------------------------------------------------------------------

function CreateFormTab({ onUseForm }: { onUseForm: (result: CreateFormResult) => void }) {
  const [month, setMonth] = useState(nextMonthValue());
  const [formType, setFormType] = useState<FormType>('comics');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CreateFormResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await createAvailsForm({ month, type: formType });
      setResult(res);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create form');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.responder_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="tab-section">
      <div className="form-card">
        <h3>Create Google Form</h3>
        <div className="controls-row">
          <div className="field-group">
            <label>Month</label>
            <input
              type="month"
              value={month}
              onChange={e => { setMonth(e.target.value); setResult(null); }}
            />
          </div>
          <div className="field-group">
            <label>Type</label>
            <div className="type-toggle">
              <button
                className={formType === 'comics' ? 'active' : ''}
                onClick={() => { setFormType('comics'); setResult(null); }}
              >Comics</button>
              <button
                className={formType === 'staff' ? 'active' : ''}
                onClick={() => { setFormType('staff'); setResult(null); }}
              >Staff</button>
            </div>
          </div>
          <button className="primary-btn" onClick={handleCreate} disabled={loading || !month}>
            {loading ? 'Creating...' : 'Create Form'}
          </button>
        </div>

        {error && (
          <div className="result-box error">
            <strong>Error</strong>
            {error}
          </div>
        )}

        {result && (
          <div className="result-box success">
            <strong>Form created — {monthValueToName(result.month)} ({result.type})</strong>
            <div className="url-result">
              <span>Responder URL:</span>
              <a href={result.responder_url} target="_blank" rel="noreferrer">
                {result.responder_url}
              </a>
              <button className="copy-btn" onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="url-result" style={{ marginTop: 6 }}>
              <span>Edit URL:</span>
              <a href={result.edit_url} target="_blank" rel="noreferrer">
                {result.edit_url}
              </a>
            </div>
            <button
              className="primary-btn"
              style={{ marginTop: 12 }}
              onClick={() => onUseForm(result)}
            >
              Use this form for Send Email →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Send Email Tab
// ------------------------------------------------------------------

function SendEmailTab({ prefill }: { prefill: CreateFormResult | null }) {
  const [month, setMonth] = useState(nextMonthValue());
  const [formType, setFormType] = useState<FormType>('comics');
  const [formUrl, setFormUrl] = useState('');
  const [formId, setFormId] = useState('');
  const [reminder, setReminder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SendEmailResult | null>(null);
  const [validationIssues, setValidationIssues] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!prefill) return;
    setFormUrl(prefill.responder_url);
    setFormId(prefill.form_id);
    setFormType(prefill.type as FormType);
    setMonth(prefill.month);
    setResult(null);
    setValidationIssues(null);
  }, [prefill]);

  const monthName = monthValueToName(month);

  const handleSend = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setValidationIssues(null);
    try {
      const res = await sendAvailsEmail({
        form_url: formUrl,
        form_id: formId || undefined,
        month: monthName,
        type: formType,
        reminder,
      });
      setResult(res);
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.issues) {
        setValidationIssues(data.issues);
      } else {
        setError(data?.error || err.message || 'Failed to send emails');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tab-section">
      <div className="form-card">
        <h3>Send Avails Email</h3>
        <div className="controls-row">
          <div className="field-group">
            <label>Month</label>
            <input
              type="month"
              value={month}
              onChange={e => { setMonth(e.target.value); setResult(null); }}
            />
          </div>
          <div className="field-group">
            <label>Type</label>
            <div className="type-toggle">
              <button
                className={formType === 'comics' ? 'active' : ''}
                onClick={() => { setFormType('comics'); setResult(null); }}
              >Comics</button>
              <button
                className={formType === 'staff' ? 'active' : ''}
                onClick={() => { setFormType('staff'); setResult(null); }}
              >Staff</button>
            </div>
          </div>
        </div>
        <div className="controls-row">
          <div className="field-group" style={{ flex: 1 }}>
            <label>Form URL (full Google Forms link)</label>
            <input
              type="url"
              value={formUrl}
              onChange={e => { setFormUrl(e.target.value); setFormId(''); setResult(null); setValidationIssues(null); }}
              placeholder="https://docs.google.com/forms/d/.../viewform"
              style={{ minWidth: 420 }}
            />
          </div>
        </div>
        <div className="controls-row">
          <label className="reminder-toggle">
            <input
              type="checkbox"
              checked={reminder}
              onChange={e => setReminder(e.target.checked)}
            />
            Reminder only (skip comics/staff who already submitted)
          </label>
          <button
            className="primary-btn"
            onClick={handleSend}
            disabled={loading || !formUrl || !month}
          >
            {loading ? 'Sending...' : reminder ? 'Send Reminder' : 'Send Email'}
          </button>
        </div>

        {validationIssues && (
          <div className="result-box error">
            <strong>Form permission issues — fix these before sending</strong>
            <ul>
              {validationIssues.map((issue, i) => <li key={i}>{issue}</li>)}
            </ul>
          </div>
        )}

        {error && (
          <div className="result-box error">
            <strong>Error</strong>
            {error}
          </div>
        )}

        {result && (
          <>
            <div className={`result-box ${result.failed > 0 ? 'warning' : 'success'}`}>
              <strong>{reminder ? 'Reminder' : 'Email'} sent — {monthName} {formType}</strong>
            </div>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-value">{result.sent}</div>
                <div className="stat-label">Sent</div>
              </div>
              {result.failed > 0 && (
                <div className="stat-item error-stat">
                  <div className="stat-value">{result.failed}</div>
                  <div className="stat-label">Failed</div>
                </div>
              )}
              {result.skipped_already_submitted > 0 && (
                <div className="stat-item">
                  <div className="stat-value">{result.skipped_already_submitted}</div>
                  <div className="stat-label">Already submitted</div>
                </div>
              )}
            </div>
            {result.failed_emails.length > 0 && (
              <div className="result-box error" style={{ marginTop: 12 }}>
                <strong>Failed addresses</strong>
                <ul>
                  {result.failed_emails.map(e => <li key={e}>{e}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Ingest Tab
// ------------------------------------------------------------------

function IngestTab() {
  const [formType, setFormType] = useState<FormType>('comics');
  const [sheetInput, setSheetInput] = useState('');
  const [clearExisting, setClearExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<IngestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleIngest = async () => {
    const sheetId = extractSheetId(sheetInput);
    if (!sheetId) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await ingestAvails({
        avails_sheet_id: sheetId,
        clear_existing: clearExisting,
        type: formType,
      });
      setResult(res);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Ingestion failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tab-section">
      <div className="form-card">
        <h3>Ingest Avails from Google Sheet</h3>
        <div className="controls-row">
          <div className="field-group">
            <label>Type</label>
            <div className="type-toggle">
              <button
                className={formType === 'comics' ? 'active' : ''}
                onClick={() => { setFormType('comics'); setResult(null); }}
              >Comics</button>
              <button
                className={formType === 'staff' ? 'active' : ''}
                onClick={() => { setFormType('staff'); setResult(null); }}
              >Staff</button>
            </div>
          </div>
        </div>
        <div className="controls-row">
          <div className="field-group" style={{ flex: 1 }}>
            <label>Response Sheet ID or URL</label>
            <input
              type="text"
              value={sheetInput}
              onChange={e => { setSheetInput(e.target.value); setResult(null); }}
              placeholder="Sheet ID or full Google Sheets URL"
              style={{ minWidth: 380 }}
            />
          </div>
        </div>
        <div className="controls-row">
          <label className="reminder-toggle">
            <input
              type="checkbox"
              checked={clearExisting}
              onChange={e => setClearExisting(e.target.checked)}
            />
            Clear existing avails before ingesting
          </label>
          <button
            className="primary-btn"
            onClick={handleIngest}
            disabled={loading || !sheetInput.trim()}
          >
            {loading ? 'Ingesting...' : 'Ingest Avails'}
          </button>
        </div>

        {error && (
          <div className="result-box error">
            <strong>Error</strong>
            {error}
          </div>
        )}

        {result && (
          <>
            <div className={`result-box ${result.success ? 'success' : 'warning'}`}>
              <strong>{result.success ? 'Ingestion complete' : 'Ingestion finished with warnings'}</strong>
              {result.message}
            </div>

            {result.staff_breakdown && result.staff_breakdown.length > 0 && (
              <table className="ingest-breakdown-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Avails</th>
                  </tr>
                </thead>
                <tbody>
                  {result.staff_breakdown.map((row: StaffBreakdownRow) => (
                    <tr key={row.email}>
                      <td>{row.name}</td>
                      <td className="avails-count">{row.avails}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {result.statistics?.errors > 0 && (
              <div className="result-box error" style={{ marginTop: 12 }}>
                <strong>{result.statistics.errors} error(s) during ingestion</strong>
              </div>
            )}
            {result.comics_not_in_db && result.comics_not_in_db.length > 0 && (
              <div className="result-box warning" style={{ marginTop: 12 }}>
                <strong>Emails not found in database</strong>
                <ul>
                  {result.comics_not_in_db.map(e => <li key={e}>{e}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Main component
// ------------------------------------------------------------------

const TABS: { id: TabId; label: string }[] = [
  { id: 'create', label: 'Create Form' },
  { id: 'send',   label: 'Send Email' },
  { id: 'ingest', label: 'Ingest' },
];

const AvailsManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('create');
  const [sendPrefill, setSendPrefill] = useState<CreateFormResult | null>(null);

  const handleUseForm = (result: CreateFormResult) => {
    setSendPrefill(result);
    setActiveTab('send');
  };

  return (
    <div className="avails-management">
      <h2>Avails Management</h2>
      <nav className="subtab-nav">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`subtab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'create' && <CreateFormTab onUseForm={handleUseForm} />}
      {activeTab === 'send'   && <SendEmailTab prefill={sendPrefill} />}
      {activeTab === 'ingest' && <IngestTab />}
    </div>
  );
};

export default AvailsManagement;
